import { request } from "@data/datoRequest"

import { pdmAssetRowsQuery } from "./graphql"
import type { PdmAssetRecord } from "../../types"

const DEFAULT_RETRIES = 2

/** Session cache: undefined = not fetched, array = success (may be empty). Failures are not cached. */
let pdmAssetRowsCache: PdmAssetRecord[] | undefined
let pdmAssetRowsInflight: Promise<PdmAssetRecord[] | null> | null = null

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function loadPdmAssetRowConfigurationsFromNetwork(
  retries: number
): Promise<PdmAssetRecord[] | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await request({
        query: pdmAssetRowsQuery,
      })
      return response?.allPdmAssets ?? null
    } catch (error) {
      const isLastAttempt = attempt === retries

      if (isLastAttempt) {
        console.warn(
          "[UpholsteryV2] Failed to fetch PDM Asset row configurations after retries:",
          error
        )
        return null
      }

      await wait(1000 * (attempt + 1))
    }
  }

  return null
}

/**
 * Fetch all Dato PDM Asset row configurations for PDM V2 row option images.
 * Returns array of pdmAsset records (Front Row, Back Row, Middle Row).
 * Returns null when the query fails or no assets are configured.
 *
 * Successful responses are cached for the tab session; concurrent callers share
 * one request. Failures are not cached so the next vehicle select can retry.
 *
 * Row images are optional enrichment; callers should continue without them when null.
 * Call `prefetchPdmAssetRowConfigurations()` from the upholstery page on mount to warm cache.
 */
export async function fetchPdmAssetRowConfigurations(options?: {
  retries?: number
}): Promise<PdmAssetRecord[] | null> {
  if (typeof window === "undefined") {
    return null
  }

  if (pdmAssetRowsCache !== undefined) {
    return pdmAssetRowsCache
  }

  if (!pdmAssetRowsInflight) {
    const retries = options?.retries ?? DEFAULT_RETRIES
    pdmAssetRowsInflight = loadPdmAssetRowConfigurationsFromNetwork(retries)
      .then((rows) => {
        if (rows !== null) {
          pdmAssetRowsCache = rows
        }
        return rows
      })
      .finally(() => {
        pdmAssetRowsInflight = null
      })
  }

  return pdmAssetRowsInflight
}

/** Start PDM asset rows fetch as soon as the upholstery experience loads (merges use cache). */
export function prefetchPdmAssetRowConfigurations(): void {
  if (typeof window === "undefined") return
  void fetchPdmAssetRowConfigurations()
}
